import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';

import dayjs from 'dayjs';
import Popover from 'antd/es/popover';
import Slider from 'antd/es/slider';

import loadable from '@common/react/loadable/loadableSettings';
import FiltersComponent from '@common/react/components/UI/FiltersComponent/FiltersPropsHandler';
import { handleUrl } from '@common/react/utils/FIltersParamsFromUrl/FiltersParamsFromUrl';
import { useItemsProviderContext } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import ResizeObserverContainer from '@common/react/components/UI/ResizeObserverContainer/ResizeObserverContainer';
import { useMobileView } from '@common/react/hooks/useMobileView';

import ImageWithSkeleton from '@commonTuna/react/components/UI/ImageWithSkeleton/ImageWithSkeleton';
import { Gender } from '@commonTuna/react/objects/Enums';
import RangePickerLoader from '@commonTuna/react/components/UI/Loaders/RangePickerLoader';
import { AmenityTypeNames } from '@commonTuna/react/objects/Amenity';
import { AmenityTypeIcons } from '@commonTuna/react/objects/AmenityImages';

import { getLocationName } from '@app/utils/Utils';
import MobileFilters from '@app/components/UI/MobileFilters/MobileFilters';
import { Doctor } from '@app/objects/Doctor';
import { ApplicationState } from '@app/store';
import { actionCreators } from '@app/store/HeaderSearch';
import Filter from '@app/components/UI/Filter/Filter';

const Datepicker = loadable(
	() => import(/* webpackChunkName: "Datepicker" */ '@commonTuna/react/components/UI/DatePicker/DatePicker'),
	{ fallback: <RangePickerLoader /> },
);

const getPopupContainer = (node) => node.closest('.doctor-card-list') || document.body;

const genders = [
	{
		value: undefined,
		label: 'All Genders',
	},
	{
		value: Gender.Male,
		label: 'Male',
	},
	{
		value: Gender.Female,
		label: 'Female',
	},
];

const filtersForClear = [
	'globalProcedureIds',
	'locationIds',
	'specialtyIds',
	'regionIds',
	'globalPayerIds',
	'gender',
	'languageIds',
	'certificateIds',
	'minExperience',
	'amenityTypes',
];

const CustomGender: React.FC<{ value, onChange }> = ({ onChange, value }) => {
	return <div key="gender-mobile" className="search-with-checkboxes mt10">
		<div className="search-with-checkboxes-title">
			<label className="filter-title">
				Genders
			</label>
			{value !== undefined ? (
				<button
					type="button"
					className="btn btn-sm search-with-checkboxes-clear"
					onClick={() => {
						onChange({ gender: undefined });
					}}
				>
					Clear
				</button>
			) : null}
		</div>
		<ul className="checkbox-list">
			{genders.map((item) => <li key={item.label}>
				<label className={`checkbox-list__item ${value === item.value ? 'checkbox-list__item__checked' : ''}`}>
					<span className="checkbox-list__item-control">
						<span className="checkbox-list__item-inner" />
						<input
							name={item.label}
							type="checkbox"
							checked={value === item.value}
							onChange={() => onChange({ gender: item.value })}
						/>
					</span>
					<span className="checkbox-list__item-name">
						{item.label}
					</span>
				</label>
			</li>)}
		</ul>
	</div>;
};

const ExperienceLevelFilter: React.FC<{ value, onChange }> = ({ value: propsValue, onChange }) => {
	const [value, setValue] = React.useState(propsValue ?? [0, 21]);
	return <div
		data-width="109"
		key="ExperienceLevel"
		className="is-relative"
	>
		<label className="slider-title">
			<i>Experience:</i>
			{' '}
			{value[0] !== 21 && value !== 20 && value[0] < 20 ? `${value[0]} Years ` : ''}
			{value[0] !== value[1] && value[0] !== 21 && value !== 20 && value[0] < 20 ? ' - ' : ''}
			{value[1] === 21 ? '20+' : value[1]}
			{' Years '}
		</label>
		<Slider
			tooltip={{ formatter: null }}
			styles={{
				track: {
					background: '#fba10d',
				},
				tracks: {
					background: '#fba10d',
				},
			}}
			step={1}
			range
			min={0}
			max={21}
			value={value}
			onChange={(value) => setValue(value)}
			onAfterChange={([minExperience, maxExperience]) => {
				onChange({
					minExperience: minExperience > 20 ? 20 : minExperience,
					maxExperience: maxExperience === 21 ? undefined : maxExperience,
				});
			}}
		/>
	</div>;
};

const Filters: React.FC<{certificates: any; languages: any; daysRange: number }> = ({ certificates, languages, daysRange: daysRangeProps }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const { searchState } = useSelector((state: ApplicationState) => ({ searchState: state.header }));
	const { setState } = bindActionCreators(actionCreators, dispatch);
	const [daysRange, setDaysRange] = React.useState(6);
	const {
		state: {
			filters,
		}, actions: { handleChange },
	} = useItemsProviderContext<Doctor>();
	const isMobile = useMobileView(768);
	const [count, setCount] = React.useState(3);

	React.useEffect(() => {
		setDaysRange(daysRangeProps);
	}, [daysRangeProps]);

	const onChange = (params) => {
		handleChange(params)
			.then(() => {
				setState({
					...searchState,
					...params,
				});
			});
		handleUrl(params, location, navigate, undefined, undefined, true);
	};

	const list = [
		<Filter
			data-width="112"
			isMobile={isMobile}
			key="Procedures"
			title="Procedures"
			type="globalProcedureListRemote"
			loadRequest="globalProcedureListRemote"
			values={filters.globalProcedureIds}
			onChange={(value) => {
				onChange({ globalProcedureIds: !value?.length ? undefined : value });
			}}
		/>,
		<Filter
			data-width="81"
			isMobile={isMobile}
			key="Clinics"
			title="Clinics"
			type="locationList"
			loadRequest="clinicPortalList"
			values={filters.locationIds}
			renderName={(option) => `${option.company ? `${option.company.name} - ` : ''}${option.nameEn}`}
			getOptionProps={(option: any) => {
				return {
					key: option.id,
					title: `${option.company ? `${option.company.name} - ` : ''}${option.nameEn}`,
					value: `${option.company ? `${option.company.portalShortName || option.company.name} - ` : ''}${option.nameEn}`,
					item: option,
					children: <>
						{option.avatar
							&& <div className="option-img__container">
								<ImageWithSkeleton className="option-img" src={`/remote/${option.avatar}`} />
							</div>
						}
						{getLocationName(option, undefined, true)}
					</>,
				};
			}}
			onChange={(value) => onChange({ locationIds: !value?.length ? undefined : value })}
		/>,
		<Filter
			data-width="107"
			isMobile={isMobile}
			key="Specialties"
			title="Specialties"
			loadRequest="specialtyPortalList"
			titleClassName="filter-title"
			type="specialtyPortalList"
			values={filters.specialtyIds || []}
			onChange={(value) => onChange({ specialtyIds: !value?.length ? undefined : value })}
		/>,
		<Filter
			data-width="101"
			isMobile={isMobile}
			key="Locations"
			title="Locations"
			loadRequest="regionList"
			titleClassName="filter-title"
			type="regionList"
			values={filters.regionIds || []}
			onChange={(value) => onChange({ regionIds: !value?.length ? undefined : value })}
		/>,
		<Filter
			data-width="109"
			isMobile={isMobile}
			key="Insurances"
			title="Insurances"
			loadRequest="globalPayerList"
			titleClassName="filter-title"
			type="globalPayerList"
			values={filters.globalPayerIds || []}
			onChange={(value) => onChange({ globalPayerIds: !value?.length ? undefined : value })}
		/>,
		isMobile ? <CustomGender onChange={onChange} value={filters.gender} key="gender" />
			: <div className="pull-left ml10" data-param="gender" data-width="88">
				<Popover
					trigger={['click']}
					content={<CustomGender onChange={onChange} value={filters.gender} key="gender" />}
					placement="bottom"
					overlayClassName="checkbox-list-popover"
				>
					<button type="button" className="checkbox-list-button">
						Gender
						{filters.gender !== undefined ? <button title="Clear" type="button" className="badge" onClick={() => onChange([])}>
							<span>1</span>
							<i className="fa fa-times" />
						</button> : null}
						<i className="fa fa-angle-down" />
					</button>
				</Popover>
			</div>,
		<Filter
			data-width="106"
			isMobile={isMobile}
			key="Languages"
			title="Languages"
			loadRequest=""
			className="hide-search"
			titleClassName="filter-title"
			type=""
			values={filters.languageIds || []}
			initItems={languages}
			initLoadedAll
			onChange={(value) => onChange({ languageIds: !value?.length ? undefined : value })}
		/>,
		<Filter
			data-width="110"
			isMobile={isMobile}
			key="Certificates"
			title="Certificates"
			loadRequest=""
			className="hide-search"
			titleClassName="filter-title"
			type=""
			values={filters.certificateIds || []}
			initItems={certificates}
			initLoadedAll
			onChange={(value) => onChange({ certificateIds: !value?.length ? undefined : value })}
		/>,
		<Filter
			data-width="110"
			isMobile={isMobile}
			key="Amenities"
			title="Amenities"
			loadRequest=""
			className="hide-search"
			titleClassName="filter-title"
			type=""
			values={filters.amenityTypes || []}
			initItems={Object.keys(AmenityTypeNames).map((key) => ({
				id: +key,
				name: AmenityTypeNames[+key],
			}))}
			renderItem={(item) => <span className="amenities-item">
				<img
					alt={item.name}
					width="30px"
					height="30px"
					className="amenities-img"
					src={AmenityTypeIcons[item.id]}
				/>
				{item.name}
			</span>}
			initLoadedAll
			onChange={(value) => onChange({ amenityTypes: !value?.length ? undefined : value })}
		/>,
		<ExperienceLevelFilter
			data-width="109"
			key="ExperienceLevel"
			onChange={onChange}
			value={[filters.minExperience ?? 0, filters.maxExperience ?? 21]}
		/>,
	];

	return <ResizeObserverContainer
		onResize={({ width }) => {
			let count = 0;
			let currentWidth = width - 300;
			for (let i = 0; i < list.length; i++) {
				if (currentWidth - +(list[i].props['data-width'] || 0) - 10 > 0) {
					count++;
					currentWidth -= +(list[i].props['data-width'] || 0) + 10;
				}
			}
			if (count === list.length - 1 && currentWidth + 100 >= +list[list.length - 1].props['data-width']) {
				count++;
			}
			if (count > list.length - 1) count = list.length - 1;
			if (count < 0) count = 0;
			if (window.innerWidth <= 768) count = 0;
			setCount(count);
		}}
		className="filters-container list-filters clearfix"
		style={{ '--len': `${daysRange * 88 + 140}px` } as any}
	>
		<div className="pull-left doctor-card-list-last-filter">
			<Datepicker
				value={searchState.from ? +searchState.from : null}
				onChange={(date) => {
					let resultDate = date;
					if (date !== null) {
						resultDate = dayjs(date).utc().startOf('day').valueOf();
					}
					onChange({ from: resultDate });
				}}
				minDateToday
				utc
			/>
		</div>
		{list.slice(0, count)}
		{count !== list.length ? <div className="pull-left ml10" style={{ width: 'max-content' }}>
			<FiltersComponent
				apply
				additionalParams={searchState}
				popoverStyle={{ minWidth: '300px' }}
				getPopupContainer={getPopupContainer}
				popoverClassName="doctor-filters-popover"
				buttonClassName="pull-right ml10"
				filtersClean={() => {
					const params = {};
					Object.values(filtersForClear.slice(count).concat('maxExperience'))
						.forEach((key) => params[key] = undefined);
					onChange({ ...params });
				}}
				triggerNode={isMobile ? undefined
					: <button type="button" className="more-filters__trigger btn btn-default">
						More Filters
					</button>}
				placement={isMobile ? undefined : null}
				getChildrenList={(tooltipValues) => tooltipValues}
				tooltipValues={
					<div
						className="filters-container clearfix filters-wrapper filters-component-title__container"
						style={{ margin: 0 }}
						data-param={filtersForClear.slice(count)}
					>
						{list.slice(count)}
					</div>}
				customNode={isMobile ? MobileFilters : undefined}
			/>
		</div> : null}
	</ResizeObserverContainer>;
};

export default Filters;
